import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { SubItem, SubItemStatus, Source, Release } from '@/types';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { NativeSelect } from '@/components/ui/native-select';
import { v4 as uuidv4 } from 'uuid';

interface InlineSubItemFormProps {
  subItem?: SubItem;
  onSubmit: (subItem: SubItem) => void;
  onCancel: () => void;
  releaseId?: string;
  availableReleases?: Release[]; // Make it optional
  currentClientId?: string; // Make it optional since it's only needed with availableReleases
}

const InlineSubItemForm: React.FC<InlineSubItemFormProps> = ({ 
  subItem, 
  onSubmit, 
  onCancel,
  releaseId,
  availableReleases = [], // Provide default empty array
  currentClientId
}) => {
  const form = useForm<Omit<SubItem, 'id'>>({
    defaultValues: {
      name: '',
      status: 'not started' as SubItemStatus,
      source: 'agency' as Source,
      approvedBy: '',
      type: '',
      sourceLink: '',
      pagesAffected: [],
      collectionsAffected: [],
      componentsAffected: [],
      notes: '',
      releaseId: releaseId || '',
    },
  });
  
  // Reset form values when subItem changes
  useEffect(() => {
    if (subItem) {
      form.reset({
        name: subItem.name,
        status: subItem.status,
        source: subItem.source,
        approvedBy: subItem.approvedBy,
        type: subItem.type,
        sourceLink: subItem.sourceLink,
        pagesAffected: subItem.pagesAffected,
        collectionsAffected: subItem.collectionsAffected,
        componentsAffected: subItem.componentsAffected,
        notes: subItem.notes,
        releaseId: subItem.releaseId,
      });
    } else {
      form.reset({
        name: '',
        status: 'not started' as SubItemStatus,
        source: 'agency' as Source,
        approvedBy: '',
        type: '',
        sourceLink: '',
        pagesAffected: [],
        collectionsAffected: [],
        componentsAffected: [],
        notes: '',
        releaseId: releaseId || '',
      });
    }
  }, [subItem, form, releaseId]);

  const handleSubmit = (data: Omit<SubItem, 'id'>) => {
    // Generate a unique ID if this is a new subitem
    const itemId = subItem?.id || uuidv4();
    
    // Only validate release if we have available releases
    if (availableReleases.length > 0 && currentClientId) {
      const selectedRelease = availableReleases.find(release => release.id === data.releaseId);
      if (!selectedRelease || selectedRelease.clientId !== currentClientId) {
        console.error('Invalid release selected or release does not belong to current client');
        return;
      }
    }
    
    const formattedSubItem: SubItem = {
      id: itemId,
      ...data,
      pagesAffected: data.pagesAffected || [],
      collectionsAffected: data.collectionsAffected || [],
      componentsAffected: data.componentsAffected || [],
      releaseId: data.releaseId
    };
    
    console.log('Submitting sub item with data:', formattedSubItem);
    onSubmit(formattedSubItem);
    
    // Reset form after submission if it's a new item
    if (!subItem) {
      form.reset({
        name: '',
        status: 'not started' as SubItemStatus,
        source: 'agency' as Source,
        approvedBy: '',
        type: '',
        sourceLink: '',
        pagesAffected: [],
        collectionsAffected: [],
        componentsAffected: [],
        notes: '',
        releaseId: releaseId || '',
      });
    }
  };

  const handlePagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pages = e.target.value.split(',').map(page => page.trim()).filter(page => page);
    form.setValue('pagesAffected', pages);
  };

  const handleCollectionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const collections = e.target.value.split(',').map(collection => collection.trim()).filter(collection => collection);
    form.setValue('collectionsAffected', collections);
  };

  const handleComponentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const components = e.target.value.split(',').map(component => component.trim()).filter(component => component);
    form.setValue('componentsAffected', components);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">
            {subItem ? 'Edit Sub Item' : 'Add Sub Item'}
          </h2>
          <p className="text-sm text-gray-500">
            {subItem ? 'Update item details' : 'Add a new item to this release'}
          </p>
        </div>

      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {availableReleases.length > 0 && (
            <FormField
              control={form.control}
              name="releaseId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Release</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Select release"
                      options={availableReleases.map(release => ({
                        value: release.id,
                        label: `${release.version} - ${new Date(release.createdAt).toLocaleDateString()}`
                      }))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Feature name or change description" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Select status"
                      options={[
                        { value: "not started", label: "Not Started" },
                        { value: "in progress", label: "In Progress" },
                        { value: "ready", label: "Ready" },
                        { value: "on hold", label: "On Hold" }
                      ]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Select source"
                      options={[
                        { value: "agency", label: "Agency" },
                        { value: "client", label: "Client" }
                      ]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="approvedBy"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Approved By</FormLabel>
                <FormControl>
                  <Input placeholder="John Smith - Some Department" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <Input placeholder="Project, Support, AdHoc" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sourceLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source Link</FormLabel>
                  <FormControl>
                    <Input placeholder="Link to ticket or project" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="pagesAffected"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pages Affected</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="About, Contact, Case Studies (comma separated)" 
                    defaultValue={field.value.join(', ')}
                    onChange={handlePagesChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="collectionsAffected"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Collections Affected</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Team Collection, News Collection (comma separated)" 
                    defaultValue={field.value.join(', ')}
                    onChange={handleCollectionsChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="componentsAffected"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Components Affected</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="CTA Component, Featured News (comma separated)" 
                    defaultValue={field.value.join(', ')}
                    onChange={handleComponentsChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <MarkdownEditor 
                    value={field.value} 
                    onChange={field.onChange}
                    placeholder="Additional details... (supports markdown formatting)" 
                    minHeight="150px"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              {subItem ? 'Update Sub Item' : 'Add Sub Item'}
            </Button>
          </div>
        </form>
      </Form>
      <div className="flex justify-end mt-4 pt-4 border-t">
        <div className="text-sm text-gray-500">
          {subItem ? 'Editing existing item' : 'Creating new item'}
        </div>
      </div>
    </div>
  );
};

export default InlineSubItemForm;
